import React, { useEffect, useRef, useState } from 'react';
import SunEditor from 'suneditor-react';
import { buttonList, setVideoOptions, setImageOptions } from './config';
import { fileUpload } from 'app/components/helpers';
import { useGetUsersList } from 'app/services/queries';
import { useGenerateContainerSasToken } from 'app/services/request-api';
import SunEditorCore from 'suneditor/src/lib/core';
import { ALLOWED_VIDEO_MIME_TYPES, Entity, BlobStorageSubfolder, StringConstants, appInsightsService, TelemetryConstants } from 'app/utils/constants';
import ReactDOMServer from 'react-dom/server';
import Link from './link';
import plugins from 'suneditor/src/plugins';
import { useSearchData, useSearchResult } from 'app/services/search-api';
import { HashTagDetail, ISearchRequestDto, SearchField, SearchOption, SearchResultDocumentResponse, SuggestDocument, SuggestResponse, TagSearchType } from 'generated/search-backend';
import { isAdmin, isSuperAdmin } from 'app/utils/authUtilities';
import { IBlobUploadParams } from 'app/models/common/post';
import useStore, { IStore } from 'app/store';
import Emojis from './emoji';
import _ from 'lodash';
import { addSasTokenToResponse } from 'app/utils/blobStorageHelpers';
import { HashTagDto, UserDto } from 'generated/request-backend';
import { GraphUser } from 'generated/user-backend';

interface IProps {
    content: string;
    isMentionAvailable?: boolean;
    setMentionedUsers?: (mentionedUsers: UserDto[]) => void;
    mentionedUsers?: UserDto[];
    hashTagsFormatted?: HashTagDto[];
    setHashTagsFormatted?: (unformatedHashTags: HashTagDto[]) => void;
    isCommentField?: boolean;
    isRequestHelpField?: boolean;
    setIsSubmitDisabled?: (isSubmitDisabled: boolean) => void;
    setTextToSubmit?: (textToSubmit: string) => void;
    textToSubmit?: string;
    fileStorageContainer: string;
    userType?: "Admin" | null;
    autoFocus?: boolean;
}

const RequestRichTextEditor: React.FC<IProps> = ({
    content,
    isMentionAvailable = false,
    setMentionedUsers,
    mentionedUsers,
    setHashTagsFormatted,
    hashTagsFormatted,
    isCommentField = false,
    isRequestHelpField = false,
    setIsSubmitDisabled,
    setTextToSubmit,
    textToSubmit,
    fileStorageContainer,
    userType,
    autoFocus = true
}) => {

    const { refetch: getToken } = useGenerateContainerSasToken({entity: StringConstants.REQUEST, containerName: fileStorageContainer});

    const userDetails: UserDto = useStore((state: IStore) => state.userDetails as UserDto);

    const editorInstance = useRef(null);

    const getEditor = (editor: SunEditorCore) => (editorInstance.current = editor);


    const videoUploadHandler = (files: File[], info: object, uploadHandler: Function) => {
        const file = files[0];
        if (!ALLOWED_VIDEO_MIME_TYPES.includes(file?.type.toLowerCase())) {
            uploadHandler('Unsupported Video format. Please upload .mp4/.ogg/.webm formats.');
            appInsightsService.logRequestflowTelemetry(TelemetryConstants.DESCRIPTION_VALIDATION_ERROR, 'request_description', {
                'error_type': 'invalid_video_format',
                'mime_type': file?.type.toLowerCase() || 'unknown'
            })
        }
        const blobName = StringConstants.NEW_BLOB + new Date().getTime();
        try {
            getToken().then((res) => {
                const blobUploadParams: IBlobUploadParams = {
                    file: file,
                    blobName: blobName,
                    container: fileStorageContainer,
                    subfolder: BlobStorageSubfolder.NONE,
                    token: res.data
                };

                fileUpload(blobUploadParams)
                    .then((res) => {
                        const response = {
                            result: [
                                {
                                    url: res._response.request.url.split(/[?#]/)[0],
                                    name: file.name,
                                    size: file.size
                                }
                            ]
                        };

                        addSasTokenToResponse(Entity.REQUEST, fileStorageContainer, response)
                        .then((res) => {
                            uploadHandler(res);
                        });
                    })
                    .catch((err) => {
                        return uploadHandler();
                    })
            });
        } catch (err) {
            return uploadHandler();
        }
    };

    const imageUploadHandler = (files: File[], info: object, uploadHandler: Function) => {
        const file = files[0];

        // Attempt to get the file extension from the mime type
        let splitFileType = file?.type?.toLowerCase().split('/');
        let imageExtension = null;
        if(splitFileType.length > 1) {
            imageExtension = splitFileType[1];
        }
        
        let blobName = StringConstants.NEW_BLOB + new Date().getTime();
        if(imageExtension) {
            blobName = blobName + "." + imageExtension;
        }

        try {
            getToken().then((res) => {
                const blobUploadParams: IBlobUploadParams = {
                    file: file,
                    blobName: blobName,
                    container: fileStorageContainer,
                    subfolder: BlobStorageSubfolder.NONE,
                    token: res.data
                };

                fileUpload(blobUploadParams)
                    .then((res) => {
                        const response = {
                            result: [
                                {
                                    url: res._response.request.url.split(/[?#]/)[0],
                                    name: file.name,
                                    size: file.size
                                }
                            ]
                        };

                        addSasTokenToResponse(Entity.REQUEST, fileStorageContainer, response)
                        .then((res) => {
                            uploadHandler(res);
                        });
                    })
                    .catch((err) => {
                        return uploadHandler();
                    })
            });
        } catch (err) {
            return uploadHandler();
        }
    };

    const [isMentionMode, setIsMentionMode] = useState<boolean>(false);
    const [isHashtagSearch, setIsHashtagSearch] = useState<boolean>(false);
    const [isSkpSearch, setIsSkpSearch] = useState<boolean>(false);
    const [skpSearchValue, setSkpSearchValue] = useState<string>('');
    const [hashtagSearchValue, setHashtagSearchValue] = useState<string>('');
    const [searchValue, setSearchValue] = useState<string>('');

    const { data: userList } = useGetUsersList(searchValue.substring(1, searchValue.length).trim(), false, userType, null, 10);
    const { mutate: searchHashtagData, data: hashtagSuggestionData } = useSearchResult();
    const { mutate: searchData, data: searchResultData } = useSearchData();

    const onTypingHandler = (event: React.KeyboardEvent<HTMLFormElement>) => {
        // init the mention scenario
        if ((event.key === '@' || event.key === '#' || event.key === '!') && !isMentionMode && !isHashtagSearch && !isSkpSearch) {
            switch (event.key) {
                case '@':
                    if (isMentionAvailable) {
                        setIsMentionMode(true);
                        setSearchValue(event.key);
                    }
                    break;
                case '#':
                    setIsHashtagSearch(true);
                    setHashtagSearchValue(event.key);
                    break;
                case '!':
                    setIsSkpSearch(true);
                    setSkpSearchValue(event.key);
                    break;
            }

            editorInstance.current.insertHTML('<span id="mention"></span>', true);
        } else if (isMentionMode || isHashtagSearch || isSkpSearch) {
            if (isMentionMode && /^[a-zA-Z\s]*$/.test(event.key) && event.key.length === 1) {
                setSearchValue(searchValue + event.key);
            } else if (isSkpSearch && event.key.length === 1) {
                setSkpSearchValue(skpSearchValue + event.key);
            } else if (isHashtagSearch && /^[a-zA-Z0-9]*$/.test(event.key) && event.key.length === 1) {
                setHashtagSearchValue(hashtagSearchValue + event.key);
            } else if (event.key === 'Backspace') {
                if (isMentionMode) {
                    setSearchValue(searchValue.slice(0, -1));
                } else if (isHashtagSearch) {
                    setHashtagSearchValue(hashtagSearchValue.slice(0, -1));
                } else if (isSkpSearch) {
                    setSkpSearchValue(skpSearchValue.slice(0, -1));
                }
            } else if (isHashtagSearch && event.key === ' ') {
                var tempContent = editorInstance.current.getContents(true);
                setHashTagsFormatted([...hashTagsFormatted, new HashTagDto({ hashTag: hashtagSearchValue.substring(1) })]);
                var anchor = tempContent.replace(
                    hashtagSearchValue,
                    `<a class="mentioned-hashtag" href="javascript:void(0);">${hashtagSearchValue}</a>`
                );
                var newContent = anchor.replace('<span id="mention"></span>', '');

                // reset state
                setIsHashtagSearch(false);
                setHashtagSearchValue('');

                setTextToSubmit(newContent);
            }
        }
    };

    useEffect(() => {
        if (hashtagSearchValue.length > 3) {
            let payload: ISearchRequestDto = {
                searchTerm: `${hashtagSearchValue.substring(1)}*`,
                pageIndex: 1,
                pageSize: 30,
                highlightPreTag: '',
                highlightPostTag: '',
                highlightFields: [],
                searchField: SearchField.Default,
                searchOption: SearchOption.HelpRequest,
                tagSearchType: TagSearchType.HashTag,
                orderBy: [],
                facets: [],
                select: ['HashTags/HashTag', 'Discriminator'],
                filter: `search.ismatch('${hashtagSearchValue.substring(1)}*', 'HashTags/HashTag')`
            };

            searchHashtagData(payload);
        } else if (hashtagSearchValue.length <= 0) {
            setIsHashtagSearch(false);

            var mentionSpan = document.getElementById('mention');
            var context = editorInstance.current.getContext();

            if (mentionSpan !== null) {
                editorInstance.current.core.removeNode(mentionSpan);
                context.link.linkController.style.display = StringConstants.NONE;
            }
        }
    }, [hashtagSearchValue]);

    useEffect(() => {
        if (skpSearchValue.length > 3) {
            searchData({
                term: skpSearchValue.substring(1),
                searchOption: SearchOption.HelpRequest,
                isAdminSearch: isAdmin(userDetails?.userRoles) || isSuperAdmin(userDetails?.userRoles),
                isParentChildSearch: false
            });
        } else if (skpSearchValue.length <= 0) {
            setIsSkpSearch(false);

            var mentionSpan = document.getElementById('mention');
            var context = editorInstance.current.getContext();

            if (mentionSpan !== null) {
                editorInstance.current.core.removeNode(mentionSpan);
                context.link.linkController.style.display = StringConstants.NONE;
            }
        }
    }, [skpSearchValue]);

    useEffect(() => {
        if (searchValue.length <= 0) {
            setIsMentionMode(false);
            var mentionSpan = document.getElementById('mention');
            var context = editorInstance.current.getContext();

            if (mentionSpan !== null) {
                editorInstance.current.core.removeNode(mentionSpan);
                context.link.linkController.style.display = StringConstants.NONE;
            }
        }
    }, [searchValue]);

    useEffect(() => {
        if (isCommentField && setIsSubmitDisabled) {
            // Handle cancel in CommentField sets textToSubmit = ''
            // We must do the same for the editor contents
            if (textToSubmit.length === 0) {
                editorInstance.current.setContents('');
            }

            setIsSubmitDisabled(editorInstance.current.getText().length <= 0);
        }

    }, [textToSubmit]);

    const MentionsListItem = (user: GraphUser) => {
        return (
            <div className="mentions-menu-item" aria-selected={true} role="option" id={user.id} data-name={user.displayName}>
                <div
                    className="mentions-menu-item-name"
                    data-name={user.displayName}
                    data-userid={user.id}
                    data-email={user.mail}>
                    {user.displayName}
                </div>
                <div
                    className="mentions-menu-item-email"
                    data-name={user.displayName}
                    data-userid={user.id}
                    data-email={user.mail}>
                    {user.mail}
                </div>
            </div>
        );
    };

    const SuggestionListItem = (item: SuggestDocument | string) => {
        return (
            <div
                className="mentions-menu-item"
                aria-selected={true}
                role="option"
                id={isSkpSearch ? (item as SuggestDocument).id : ''}
                data-name={isSkpSearch ? (item as SuggestDocument).unifiedTrackingId.toString() : item}>
                <div className="mentions-menu-item-name" data-name={isSkpSearch ? (item as SuggestDocument).unifiedTrackingId : item}>
                    {isSkpSearch ? `${(item as SuggestDocument).unifiedTrackingId}: ${(item as SuggestDocument).title}` : item}
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (editorInstance.current !== null) {
            var editor = editorInstance.current as SunEditorCore;
            var context = editorInstance.current.getContext();
            var mentionSpan = document.getElementById('mention');

            if (
                (isMentionMode && userList?.length > 0) ||
                (isSkpSearch && searchResultData?.length > 0) ||
                (isHashtagSearch && hashtagSuggestionData?.searchResult?.items?.length > 0)
            ) {
                // display the dialog that has suggestions
                context.link.linkController.style.display = 'block';
                context.link.linkController.style.top = mentionSpan?.offsetTop + 27 + 'px';
                context.link.linkController.style.left = mentionSpan?.offsetLeft - 32 + 'px';

                var dataDivs;

                if (isMentionMode) {
                    dataDivs = userList.map((user: GraphUser) => ReactDOMServer.renderToString(MentionsListItem(user)));
                } else if (isSkpSearch) {
                    dataDivs = searchResultData.map((item: SuggestResponse) =>
                        ReactDOMServer.renderToString(SuggestionListItem(item.document))
                    );
                } else if (isHashtagSearch) {
                    var hashtags: string[] = [];

                    hashtagSuggestionData.searchResult.items.forEach((item: SearchResultDocumentResponse) => {
                        if (item?.document?.hashTags && item?.document?.hashTags.length > 0) {
                            hashtags.push(...item?.document?.hashTags?.map((tag: HashTagDetail) => tag.hashTag));
                        }
                    });

                    hashtags = _.uniq(hashtags);

                    dataDivs = hashtags.map((hashtag) => ReactDOMServer.renderToString(SuggestionListItem(hashtag)));
                }

                if(isMentionMode) {
                    // as we are currently getting all the users, users list is overlapping the footer.
                    // So restricting the height and enabling scroll
                    context.link.linkController.children[1].style.maxHeight = '300px';
                    context.link.linkController.children[1].style.overflowY = 'auto';
                }
                
                // the content container in which the user divs will be viewed
                context.link.linkController.children[1].innerHTML = dataDivs.join('');

                for (var i = 0; i < context.link.linkController.children[1].children.length; i++) {
                    var child : HTMLDivElement = context.link.linkController.children[1].children.item(i);

                    // add on click handlers for each suggestion
                    child.onclick = (event: MouseEvent) => {
                        event.preventDefault();
                        event.stopPropagation();

                        var currentContent = editorInstance.current.getContents(true);
                        var addAnchor;

                        if (isMentionMode) {
                            addAnchor = currentContent.replace(
                                searchValue,
                                `<a class="mentioned-user">${(event?.target as HTMLDivElement)?.dataset?.name}</a>&nbsp;`
                            );
                            const userIds = mentionedUsers.map((user) => user.id);

                            // ensure uniqueness of mentioned users
                            if (!userIds.includes((event?.target as HTMLDivElement)?.dataset?.userid)) {
                                setMentionedUsers([
                                    ...mentionedUsers,
                                    new UserDto({
                                        id: (event?.target as HTMLDivElement)?.dataset?.userid,
                                        displayName: (event?.target as HTMLDivElement)?.dataset?.name,
                                        email: (event?.target as HTMLDivElement)?.dataset?.email
                                    })
                                ]);
                            }
                        } else if (isSkpSearch) {
                            addAnchor = currentContent.replace(
                                skpSearchValue,
                                `<a class="mentioned-request" href="/request-detail/${(event?.target as HTMLDivElement)?.dataset?.name}">${(event?.target as HTMLDivElement)?.innerText}</a>&nbsp;`
                            );
                        } else if (isHashtagSearch) {
                            addAnchor = currentContent.replace(
                                hashtagSearchValue,
                                `<a class="mentioned-hashtag">#${(event?.target as HTMLDivElement)?.innerText}</a>&nbsp`
                            );
                            setHashTagsFormatted([...hashTagsFormatted, new HashTagDto({ hashTag: (event?.target as HTMLDivElement)?.innerText })]);
                        }

                        // remove placeholder and set the content of the editor
                        var newContent = addAnchor.replace('<span id="mention"></span>', '');
                        context.element.wysiwyg.innerHTML = newContent;
                        setTextToSubmit(newContent);

                        // ensure suggestion modal disappears
                        context.link.linkController.style.display = StringConstants.NONE;

                        // reset state
                        setIsMentionMode(false);
                        setIsSkpSearch(false);
                        setIsHashtagSearch(false);
                        setSkpSearchValue('');
                        setSearchValue('');
                        setHashtagSearchValue('');

                        editor.core.focusEdge(null);
                    };
                }
            } else {
                // if anchor does not exist, we will hide the dialog
                if (context?.link?.linkController) {
                    context.link.linkController.style.display = StringConstants.NONE;
                }
            }
        }
    }, [userList, searchResultData, hashtagSuggestionData, editorInstance, isMentionMode, isSkpSearch, isHashtagSearch]);

    // Deleting whole search term scenario
    // If the user highlights and deletes the whole phrase, disable mention mode and reset state
    const onInputHandler = (event: React.KeyboardEvent<HTMLFormElement>) => {
        // if at mention mode is true and the text content does not include the seachValue
        if (isMentionMode && !editorInstance.current.getText().includes(searchValue)) {
            setTextToSubmit((event.target as HTMLFormElement).innerHTML);
            if (event.key !== ' ') {
                setIsMentionMode(false);
                setSearchValue('');
            }
        } else if (isSkpSearch && !editorInstance.current.getText().includes(skpSearchValue)) {
            setTextToSubmit((event.target as HTMLFormElement).innerHTML);
            setIsSkpSearch(false);
            setSkpSearchValue('');
        } else if (isHashtagSearch && !editorInstance.current.getText().includes(hashtagSearchValue)) {
            setTextToSubmit((event.target as HTMLFormElement).innerHTML);
            setIsHashtagSearch(false);
            setHashtagSearchValue('');
        }
    };

    useEffect(() => {
        if (editorInstance.current) {
            const editor = editorInstance.current as SunEditorCore;
            const buttons = editor.core.context.element.toolbar.getElementsByClassName('se-btn se-tooltip');
            Array.prototype.forEach.call(
                buttons,
                (btn: Element) => {
                    btn.getAttribute('disabled') !== 'true' && btn.setAttribute('tabindex', '0');
                    btn.setAttribute('role', 'button');
                    const btnText = btn.getAttribute('data-command');
                    if(btnText) {
                        btn.setAttribute('aria-label', `${btnText} button`);
                    }
                }
            );
        }
    }, [editorInstance])

    return (
        <div>
            <form onKeyDown={onTypingHandler} onKeyUp={onInputHandler}>
                <SunEditor
                    setContents={content}
                    setOptions={{
                        mode: 'classic',
                        plugins: { ...plugins, Link, Emojis },
                        buttonList: isRequestHelpField  
                            ? [...buttonList.requestHelp]
                            : [...buttonList.responsive],
                        ...setVideoOptions,
                        ...setImageOptions,
                        attributesWhitelist: {
                            all: 'style',
                            input: 'checked'
                        },
                        minHeight: isCommentField ? '5rem' : '500px',
                        tabDisable: true,
                    }}
                    onChange={setTextToSubmit}
                    onVideoUploadBefore={videoUploadHandler}
                    onImageUploadBefore={imageUploadHandler}
                    getSunEditorInstance={getEditor}
                    autoFocus={autoFocus}
                    onFocus={() => {
                        appInsightsService.logRequestflowTelemetry(TelemetryConstants.DESCRIPTION_EDITOR_FOCUSED, 'request_description', {});
                    }}
                />
            </form>
        </div>
    );
};

export default RequestRichTextEditor;
